import React, { useEffect, useState } from "react";
import { formatSubjectName } from "../../../layouts/schoolPortal/schoolPortalTheme";
import {
  Dialog, Box, Typography, IconButton, CircularProgress, Chip,
  Button, TextField, FormControlLabel, Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import moment from "moment";
import { useSnackbar } from "notistack";
import { get_session_agenda_api, post_session_summary_api } from "../../../DAL/Tutors/Tutors";
import SessionAmend from "./SessionAmend";
import BookingActions from "./BookingActions";
import { get_session_record_api, generate_session_recap_api } from "../../../DAL/SchoolPortal/Tutors";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  success, warning, error as errorColor,
  inputSx, primaryButtonSx, secondaryButtonSx,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * The pre-session brief.
 *
 * `GET /api/tutors/bookings/:id/agenda` has existed since the session engine
 * was built, is described in the platform spec as the reason an instructor
 * never walks into a session cold, and had no client anywhere in either portal.
 * It was reachable by nobody.
 *
 * IT IS ALSO THE DEBRIEF
 * `POST /api/tutors/bookings/:id/summary` had a DAL function and no caller
 * anywhere in either portal. Nothing could reach it, so no summary was ever
 * written from the app — which matters more than it sounds: the summary seeds
 * the NEXT agenda's "last time" and creates the student's post-session tasks.
 * Without it every briefing said "No summary was written for that session" for
 * ever, and the follow-up sequence never ran.
 *
 * Before and after are the same screen on purpose. The instructor already has
 * this open with the focus list in front of them; asking them to go and find a
 * different surface to record what happened is how it stops happening.
 *
 * IT IS ALLOWED TO SAY IT DOES NOT KNOW
 * The agenda carries its own `confidence` and a `basis` sentence explaining
 * what it is derived from — "only 6 practice answers so far, not enough to rank
 * reliably; ask them where they feel shaky". That is rendered as prominently as
 * the focus list, because an instructor who is told a ranking is thin will ask
 * the student, and one who is handed a confident-looking empty list will not.
 */

const lbl = {
  fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", color: textSecondary,
};

const CONFIDENCE = {
  high: { color: success, text: "Based on plenty of practice data" },
  medium: { color: warning, text: "Based on limited practice data" },
  low: { color: textMuted, text: "Not enough practice data to rank reliably" },
};

export default function SessionPrep({ booking, open, onClose, onDone, onJoin, region }) {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [writing, setWriting] = useState(false);
  // The recording of the session being debriefed. Fetched separately from the
  // agenda because it arrives on a webhook minutes to hours after the room
  // closes — an instructor opening this straight afterwards will correctly see
  // nothing, and one opening it the next morning will see it.
  const [record, setRecord] = useState(null);
  const [body, setBody] = useState("");
  const [followUp, setFollowUp] = useState(true);
  // THE LESSON DECISION IS MADE, NOT DEFAULTED. This was a checkbox that
  // started ticked, so an instructor who saved without looking signed the
  // lesson off — the one human judgement the gated course depends on was
  // opt-out (US_PART61_PLATFORM_SPEC.md §6.5). Null until they choose, and
  // "not yet" is as cheap as yes but has to say why.
  const [decision, setDecision] = useState(null);
  const [notYetReason, setNotYetReason] = useState("");
  // The body came from the recording rather than the keyboard. Sent with the
  // debrief so the record says a human approved a draft, not that they wrote it.
  const [drafted, setDrafted] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Has it happened yet? The brief is read before, and written after, and
  // which half leads is decided by the clock rather than by a mode the
  // instructor has to pick.
  const ended = booking?.start_time ? new Date(booking.start_time) < new Date() : false;

  useEffect(() => { if (!open) { setWriting(false); setBody(""); setFollowUp(true); setRecord(null); setDecision(null); setNotYetReason(""); setDrafted(false); } }, [open]);

  useEffect(() => {
    if (!open || !booking?._id) return;
    get_session_record_api(booking._id).then((r) => {
      if (r.code === 200) setRecord(r.data || r);
    }).catch(() => { /* a missing recording is a normal state, not an error */ });
  }, [open, booking?._id]);

  useEffect(() => {
    if (!open || !booking?._id) return;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    get_session_agenda_api(booking._id)
      .then((r) => {
        if (cancelled) return;
        if (r.code === 200) setState({ loading: false, data: r.data, error: null });
        else setState({ loading: false, data: null, error: r.message || "Could not build an agenda" });
      })
      .catch(() => !cancelled && setState({ loading: false, data: null, error: "Could not build an agenda" }));
    return () => { cancelled = true; };
  }, [open, booking?._id]);

  const a = state.data;
  const conf = CONFIDENCE[a?.confidence] || CONFIDENCE.low;

  /**
   * DRAFTED, NOT COMPOSED (spec §6.6). The session was recorded and
   * transcribed, and the recap already says what was covered — so the debrief
   * opens with that in it and the instructor corrects it, instead of facing an
   * empty box at 19:05. Saving is still the instructor's act; nothing is
   * published without them.
   */
  const recap = record?.recap;
  const asText = (k) => (typeof k === "string" ? k : k?.text || k?.point || "");
  const recapReady = recap?.status === "ready" && Boolean(recap.summary || recap.keyPoints?.length);
  const composeDraft = (r) => [
    r.summary,
    r.keyPoints?.length ? `What we covered:\n${r.keyPoints.map((k) => `- ${asText(k)}`).join("\n")}` : "",
    r.weakAreas?.length ? `Still shaky: ${r.weakAreas.map(asText).join(", ")}.` : "",
    r.nextFocus ? `Next time: ${r.nextFocus}` : "",
  ].filter(Boolean).join("\n\n");

  const startWriting = () => {
    if (!body.trim() && recapReady) { setBody(composeDraft(recap)); setDrafted(true); }
    setWriting(true);
  };

  // A transcript with no recap yet: build one on request rather than making
  // the instructor wait for a job they cannot see.
  const draftFromRecording = async () => {
    setDrafting(true);
    try {
      const g = await generate_session_recap_api(booking._id);
      const fresh = await get_session_record_api(booking._id);
      const rec = fresh?.data || fresh;
      if (fresh?.code === 200) setRecord(rec);
      const r = rec?.recap;
      if (r?.status === "ready" && (r.summary || r.keyPoints?.length)) {
        setBody(composeDraft(r));
        setDrafted(true);
      } else {
        enqueueSnackbar(g?.message || "The recording could not be drafted yet — write this one yourself.", { variant: "info" });
      }
    } catch (e) {
      enqueueSnackbar("Could not draft from the recording", { variant: "error" });
    } finally {
      setDrafting(false);
    }
  };

  return (
    // A DIALOG, NOT A DRAWER. This opened as a right-hand panel slid in over
    // the right-hand strip bay — a sidebar covering a sidebar, at sidebar
    // width, for the one surface on this page that is dense enough to want
    // room: the focus list, what happened last time, the open tasks, and a
    // debrief you are expected to type into. It is the brief, so it takes the
    // middle of the screen.
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper"
      PaperProps={{ sx: { borderRadius: "14px", p: 2, backgroundColor: "#FAF8F5" } }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Typography sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800, color: textPrimary, flex: 1 }}>
          {ended ? "This session" : "Before this session"}
        </Typography>
        {/* WHEN IT IS NEARLY TIME, JOINING IS THE POINT OF THE SCREEN.
            `join` is computed server-side by the same
            utils/session_join_window.js the video endpoint enforces with, so
            this button can never be offered when the server would refuse it —
            nor hidden when it would not. The instructor's window opens THIRTY
            minutes before, not ten: ten is the student's, and a teacher is
            meant to be in the room first. */}
        {booking?.join?.applicable && booking.join.open && onJoin && (
          <Button
            onClick={() => onJoin(booking)}
            startIcon={<VideocamIcon sx={{ fontSize: 15 }} />}
            sx={{ ...primaryButtonSx, minHeight: 0, py: 0.5, px: 1.4, fontSize: "12.5px", mr: 0.5 }}
          >
            Join now
          </Button>
        )}
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      {booking?.join?.applicable && booking.join.state === "too_early"
        && new Date(booking.join.opensAt).getTime() - Date.now() < 2 * 60 * 60 * 1000 && (
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mb: 0.5 }}>
          You can join from {moment(booking.join.opensAt).format("HH:mm")}, ten minutes before.
        </Typography>
      )}
      <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mb: 2 }}>
        {booking?.studentName}
        {booking?.start_time ? ` · ${moment(booking.start_time).format("ddd D MMM, HH:mm")}` : ""}
        {a?.subjectLabel ? ` · ${a.subjectLabel}` : ""}
      </Typography>

      {state.loading && <CircularProgress size={18} sx={{ color: accent }} />}
      {state.error && (
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: errorColor }}>{state.error}</Typography>
      )}

      {a && (
        <>
          {/* How much to trust what follows, before what follows. */}
          <Box sx={{ p: 1.25, mb: 2, borderRadius: "8px", border: `1px solid ${conf.color}44`, backgroundColor: `${conf.color}0d` }}>
            <Typography sx={{ fontFamily: font, fontSize: "12px", fontWeight: 700, color: conf.color }}>
              {conf.text}
            </Typography>
            {a.basis && (
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.35, lineHeight: 1.45 }}>
                {a.basis}
              </Typography>
            )}
          </Box>

          <Typography sx={{ ...lbl, mb: 0.75 }}>What to work on</Typography>
          {(a.focus || []).length ? (a.focus || []).map((f, i) => (
            <Box key={i} sx={{ mb: 1.25, pb: 1.25, borderBottom: `1px solid ${border}` }}>
              <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
                {f.label || f.topic}
              </Typography>
              {f.why && (
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mt: 0.25, lineHeight: 1.45 }}>
                  {f.why}
                </Typography>
              )}
              {(f.acsCodes || []).length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.6 }}>
                  {f.acsCodes.map((c) => (
                    <Chip key={c} label={c} size="small" sx={{
                      height: 18, fontSize: "10px", fontFamily: font, fontWeight: 700, borderRadius: "4px",
                      backgroundColor: "rgba(37,99,235,0.08)", color: "#2563eb",
                    }} />
                  ))}
                </Box>
              )}
            </Box>
          )) : (
            <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 2, lineHeight: 1.5 }}>
              Nothing stands out from their practice yet. Open with what they think is hardest.
            </Typography>
          )}

          {a.lastSession && (
            <>
              <Typography sx={{ ...lbl, mt: 2, mb: 0.75 }}>Last time</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted, mb: 0.35 }}>
                {moment(a.lastSession.endedAt).format("D MMM")}
                {a.lastSession.subject ? ` · ${formatSubjectName(a.lastSession.subject)}` : ""}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.5 }}>
                {a.lastSession.summary || "No summary was written for that session."}
              </Typography>
              {a.lastSession.outstanding > 0 && (
                <Typography sx={{ fontFamily: font, fontSize: "12px", color: warning, mt: 0.5 }}>
                  {a.lastSession.outstanding} task{a.lastSession.outstanding === 1 ? "" : "s"} from it still outstanding.
                </Typography>
              )}
            </>
          )}

          {(a.openTasks || []).length > 0 && (
            <>
              <Typography sx={{ ...lbl, mt: 2, mb: 0.75 }}>Set for them</Typography>
              {a.openTasks.map((t, i) => (
                <Box key={i} sx={{ display: "flex", gap: 0.75, py: 0.4 }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: accent, mt: 0.75, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.4 }}>
                      {t.title}
                    </Typography>
                    {t.dueAt && (
                      <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: new Date(t.dueAt) < new Date() ? errorColor : textMuted }}>
                        due {moment(t.dueAt).format("D MMM")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </>
          )}

          {a.studentNote && (
            <>
              <Typography sx={{ ...lbl, mt: 2, mb: 0.5 }}>They asked for</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.5, fontStyle: "italic" }}>
                “{a.studentNote}”
              </Typography>
            </>
          )}

          {/* ── after it has run ────────────────────────────────────────── */}
          {ended && (
            <Box sx={{ mt: 2.5, pt: 2, borderTop: `2px solid ${border}` }}>
              {/* P7 says nothing the student paid for is ever lost. It was
                  being stored and had no route and no client, which is the
                  same as losing it. The URL is minted per request and expires,
                  so it is fetched here rather than kept. */}
              {record?.recording?.available && (
                <Box sx={{ mb: 1.5 }}>
                  <a
                    href={record.recording.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: accent, textDecoration: "none" }}
                  >
                    ▸ Watch the recording
                  </a>
                  {record.transcript?.available && (
                    <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mt: 0.3 }}>
                      A transcript is attached to this session.
                    </Typography>
                  )}
                </Box>
              )}
              {record?.recording && !record.recording.available && record.recording.reason && (
                <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mb: 1.5 }}>
                  No recording — {record.recording.reason}
                </Typography>
              )}
              {a.summaryWritten ? (
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: success, fontWeight: 700 }}>
                  Debriefed. Their tasks went out.
                </Typography>
              ) : writing ? (
                <>
                  {drafted ? (
                    <Box sx={{ backgroundColor: `${warning}12`, border: `1px solid ${warning}44`, borderRadius: "8px", px: 1.25, py: 0.85, mb: 1 }}>
                      <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textPrimary, lineHeight: 1.5 }}>
                        Drafted from the recording. Check it and correct it — saving puts your name to it, and they will read it.
                      </Typography>
                    </Box>
                  ) : record?.transcript?.available && !recapReady ? (
                    <Button onClick={draftFromRecording} disabled={drafting} sx={{ ...secondaryButtonSx, width: "100%", mb: 1 }}>
                      {drafting ? "Drafting from the recording…" : "Draft it from the recording"}
                    </Button>
                  ) : null}
                  <Typography sx={{ ...lbl, mb: 0.75 }}>What happened</Typography>
                  <TextField
                    size="small" fullWidth multiline minRows={4} autoFocus
                    value={body} onChange={(e) => setBody(e.target.value)} sx={{ ...inputSx, mb: 1 }}
                    placeholder="Worked the W&B arithmetic. Picks the right answer with the wrong working — set him three CG problems before next time."
                  />
                  {/* The student sees this. It is the one thing they take away,
                      and it is what the next briefing opens with. */}
                  <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mb: 1, lineHeight: 1.45 }}>
                    They will read this, and it is what your next briefing on them opens with.
                  </Typography>
                  <FormControlLabel
                    control={<Checkbox checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} size="small" sx={{ color: accent, "&.Mui-checked": { color: accent } }} />}
                    label={
                      <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary }}>
                        Set their follow-up work from what you covered
                      </Typography>
                    }
                    sx={{ mb: 0.25, display: "block" }}
                  />
                  {/* ── the gate ──────────────────────────────────────────
                      On the US course a lesson does not open because the last
                      one was read; it opens because someone checked it landed.
                      That decision belongs HERE, not on a second screen: the
                      instructor has just written what they covered, and whether
                      the student is ready is what they were deciding while they
                      typed it. A separate action after the session is mentally
                      over is one that quietly stops happening — and then every
                      student stalls with nobody able to say why. */}
                  {booking?.lesson != null && (
                    <Box sx={{ mt: 0.75, mb: 1.25 }}>
                      <Typography sx={{ ...lbl, mb: 0.6 }}>Lesson {booking.lesson} — your call</Typography>
                      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                        {[
                          { v: "signed_off", t: `Sign it off — opens Lesson ${booking.lesson + 1}`, c: success },
                          { v: "not_yet", t: "Not yet — they need another go", c: warning },
                        ].map((o) => (
                          <Box
                            key={o.v} role="button" tabIndex={0}
                            onClick={() => setDecision(o.v)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDecision(o.v); } }}
                            sx={{
                              flex: "1 1 180px", cursor: "pointer", px: 1.25, py: 0.9, borderRadius: "8px",
                              border: `1.5px solid ${decision === o.v ? o.c : border}`,
                              backgroundColor: decision === o.v ? `${o.c}14` : "#fff",
                              fontFamily: font, fontSize: "12.5px", fontWeight: decision === o.v ? 700 : 500,
                              color: decision === o.v ? o.c : textPrimary,
                            }}
                          >
                            {o.t}
                          </Box>
                        ))}
                      </Box>
                      {decision === "not_yet" && (
                        <TextField
                          size="small" fullWidth multiline minRows={2} autoFocus
                          label="What do they still need?" value={notYetReason}
                          onChange={(e) => setNotYetReason(e.target.value)}
                          placeholder="Still guessing at the crosswind component — one more go at it before I sign it off."
                          helperText="They are told this. It is what the next session is for."
                          FormHelperTextProps={{ sx: { fontFamily: font, fontSize: "11px", color: textMuted, ml: 0.5 } }}
                          sx={{ ...inputSx, mt: 1 }}
                        />
                      )}
                    </Box>
                  )}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      disabled={saving}
                      onClick={async () => {
                        if (!body.trim()) {
                          enqueueSnackbar("Say what happened — the debrief needs a body, and so does the student", { variant: "warning" });
                          return;
                        }
                        if (booking?.lesson != null && !decision) {
                          enqueueSnackbar(`Decide on Lesson ${booking.lesson} — sign it off, or say what they still need`, { variant: "warning" });
                          return;
                        }
                        if (decision === "not_yet" && !notYetReason.trim()) {
                          enqueueSnackbar("Say what they still need — that is what the next session is for", { variant: "warning" });
                          return;
                        }
                        setSaving(true);
                        try {
                          const r = await post_session_summary_api(booking._id, {
                            body: body.trim(),
                            createFollowUp: followUp,
                            lessonDecision: booking?.lesson != null
                              ? { lesson: booking.lesson, outcome: decision, reason: decision === "not_yet" ? notYetReason.trim() : "" }
                              : null,
                            // For a server that predates lessonDecision.
                            signOffLesson: booking?.lesson != null && decision === "signed_off" ? booking.lesson : null,
                            draftedFromTranscript: drafted,
                            // The focus list is already ACS-tagged, so the tasks
                            // land against the same elements the session was
                            // built from rather than an untagged guess.
                            acsCodes: (a.focus || []).flatMap((f) => f.acsCodes || []),
                          });
                          if (r.code === 200) {
                            const n = r.data?.tasksCreated || 0;
                            const opened = r.data?.signedOffLesson;
                            enqueueSnackbar(
                              opened
                                ? `Saved. Lesson ${opened + 1} is open for them${n ? `, and ${n} task${n === 1 ? "" : "s"} set` : ""}.`
                                : decision === "not_yet"
                                  ? `Saved. Lesson ${booking.lesson} is not signed off, and they've been told what's left.`
                                  : n ? `Saved. ${n} task${n === 1 ? "" : "s"} set for them.` : "Saved.",
                              { variant: "success" },
                            );
                            setWriting(false);
                            onDone?.();
                            onClose();
                          } else enqueueSnackbar(r.message || "That didn't save", { variant: "error" });
                        } finally { setSaving(false); }
                      }}
                      sx={{ ...primaryButtonSx, flex: 1 }}
                    >
                      {saving ? "Saving…" : "Save it"}
                    </Button>
                    <Button onClick={() => setWriting(false)} sx={secondaryButtonSx}>Cancel</Button>
                  </Box>
                </>
              ) : (
                <>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 1, lineHeight: 1.5 }}>
                    No debrief yet. Without it their follow-up work never goes out, and your next
                    briefing on them opens with a blank.
                  </Typography>
                  <Button onClick={startWriting} sx={{ ...primaryButtonSx, width: "100%" }}>
                    {recapReady ? "Debrief this session — a draft is ready" : "Debrief this session"}
                  </Button>
                </>
              )}
            </Box>
          )}
        </>
      )}
      {/* A BRIEF THAT CANNOT ACT ON THE SESSION IS A PRINTOUT.
          This screen knew everything about the session and could do nothing to
          it: not hand it to a colleague, not change what it covers, not even
          reach the move/cancel that already existed — those lived in an
          overflow menu on a row somewhere behind this dialog. Once you are
          looking at a session, the things you might do to it should be here.

          Only while it is still ahead of you. Handing over or re-topicking a
          session that has happened edits history rather than a plan, and the
          server refuses it — so the buttons do not appear either. */}
      {booking && !ended && (
        <SessionAmend
          booking={booking}
          region={region}
          onDone={() => { onDone?.(); onClose?.(); }}
          extra={<BookingActions booking={booking} canTeach onDone={() => { onDone?.(); onClose?.(); }} />}
        />
      )}
    </Dialog>
  );
}
